import { AlloyLegacyThemeDesignTokens, WeaveTheme } from '../types';
import { ThemeSemanticTokens } from '../types/semantic';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

export const getSemanticTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): ThemeSemanticTokens => ({
  colors: theme.colors,
  shadows: theme.shadows,
  halo: {
    default: {
      focus: theme.halos.focus,
      hover: theme.halos.hover,
      pressed: theme.halos.active,
    },
  },
});

const mapWeaveColors = (tokens: WeaveTokens): ThemeSemanticTokens['colors'] => {
  const color = tokens?.generic?.color;
  return {
    black: color?.black?.[100]?.[100] /* #000000 */,
    white: color?.white?.[100] /* #ffffff */,

    adskBlue050: color?.slate?.[100]?.[100] /* #f9f9f9 - closest match */,
    adskBlue100: color?.blue?.[100]?.[100] /* #cdeaf7 */,
    adskBlue300: color?.blue?.[200]?.[100] /* #9bd5ef */,
    adskBlue500: color?.blue?.[500]?.[100] /* #0696d7 */,
    adskBlue600: color?.blue?.[600]?.[100] /* #007fc6 */,
    adskBlue700: color?.blue?.[700]?.[100] /* #006eaf */,

    charcoal050: color?.slate?.[150]?.[100] /* #f2f2f2 - closest match */,
    charcoal100: color?.charcoal?.[100]?.[100] /* #eeeeee */,
    charcoal200: color?.charcoal?.[200]?.[100] /* #dcdcdc */,
    charcoal300: color?.charcoal?.[300]?.[100] /* #cccccc */,
    charcoal400: color?.charcoal?.[400]?.[100] /* #bbbbbb */,
    charcoal500: color?.charcoal?.[500]?.[100] /* #999999 */,
    charcoal600: color?.charcoal?.[600]?.[100] /* #808080 */,
    charcoal700: color?.charcoal?.[700]?.[100] /* #666666 */,
    charcoal800: color?.charcoal?.[800]?.[100] /* #4d4d4d */,
    charcoal900: color?.charcoal?.[900]?.[100] /* #3c3c3c */,

    red100: color?.red?.[100]?.[100] /* #faeded */,
    red300: color?.red?.[300]?.[100] /* #f9b4b4 */,
    red500: color?.red?.[500]?.[100] /* #eb5555 */,
    red600: color?.red?.[600]?.[100] /* #dd2222 */,
    red700: color?.red?.[700]?.[100] /* #af1b1b */,

    pink300: color?.pink?.[300]?.[100] /* #ffb8db */,
    pink500: color?.pink?.[600]?.[100] /* #e84396 */,
    pink700: color?.pink?.[700]?.[100] /* #c72877 */,

    purple300: color?.purple?.[300]?.[100] /* #d5bcf7 */,
    purple500: color?.purple?.[600]?.[100] /* #a76ef5 */,
    purple700: color?.purple?.[700]?.[100] /* #8558c5 */,

    yellowOrange100: color?.orange?.[100]?.[100] /* #feecd1 */,
    yellowOrange300: color?.orange?.[200]?.[100] /* #fddaa4 */,
    yellowOrange500: color?.orange?.[500]?.[100] /* #faa21b */,
    yellowOrange700: color?.orange?.[700]?.[100] /* #d9730b */,

    green100: color?.green?.[100]?.[100] /* #e7f2d9 */,
    green300: color?.green?.[300]?.[100] /* #b7d78c */,
    green500: color?.green?.[500]?.[100] /* #87b340 */,
    green700: color?.green?.[700]?.[100] /* #507b16 */,

    darkBlue100: color?.navy?.[100]?.[100] /* #e1ebf7 */,
    darkBlue300: color?.navy?.[300]?.[100] /* #8babdc */,
    darkBlue500: color?.navy?.[500]?.[100] /* #3970b9 */,
    darkBlue700: color?.navy?.[700]?.[100] /* #064285 */,
    darkBlue900Alpha45:
      color?.navy?.[900]?.[100] /* #0c2c54 - full opacity fallback */,

    turquoise100: color?.turquoise?.[100]?.[100] /* #d6f2ef */,
    turquoise300: color?.turquoise?.[300]?.[100] /* #84d7ce */,
    turquoise500: color?.turquoise?.[500]?.[100] /* #32bcad */,
    turquoise700: color?.turquoise?.[700]?.[100] /* #168576 */,

    salmon300: color?.salmon?.[300]?.[100] /* #ffbeb3 */,
    salmon500: color?.salmon?.[500]?.[100] /* #f26a52 */,
    salmon700: color?.salmon?.[700]?.[100] /* #a84a39 */,

    brown300: color?.yellow?.[700]?.[100] /* #d7cb1d - closest match */,
    brown500: color?.yellow?.[800]?.[100] /* #b8a714 - closest match */,
    brown700: color?.yellow?.[900]?.[100] /* #887306 - closest match */,

    hoverHalo: color?.charcoal?.[600]?.[15] /* #80808026 */,
    activeHalo: color?.charcoal?.[600]?.[25] /* #80808040 */,
    focusHalo: color?.blue?.[500]?.[35] /* #0696d759 */,

    acsLogoBlue: color?.navy?.[600]?.[100] /* #1858a8 */,
    adskBlue500Alpha15: color?.blue?.[400]?.[15] /* #38abdf26 */,
    adskBlue300Alpha35:
      color?.blue?.[200]?.[70] /* #9bd5efb3 - closest alpha match */,
    green300Alpha35:
      color?.green?.[300]?.[100] /* #b7d78c - full opacity fallback */,
    yellowOrange300Alpha35:
      color?.orange?.[200]?.[100] /* #fddaa4 - full opacity fallback */,
    red300Alpha35:
      color?.red?.[300]?.[100] /* #f9b4b4 - full opacity fallback */,

    /** @deprecated use Weave 2.0 theme colors */
    _greyAlpha: undefined,
    /** @deprecated use Weave 2.0 theme colors */
    _adskBlue100Alpha: undefined,
    /** @deprecated use Weave 2.0 theme colors */
    _blueShadow: undefined,
  };
};

export const getSemanticTokensWeave = (
  tokens: WeaveTokens,
): ThemeSemanticTokens => ({
  colors: mapWeaveColors(tokens),
  shadows: {
    highElevation: tokens.semantic?.shadow?.high,
    lowElevation: tokens.semantic?.shadow?.low,
  },
  halo: {
    default: {
      focus: tokens.semantic?.halo?.default?.focus,
      hover: tokens.semantic?.halo?.default?.hover,
      pressed: tokens.semantic?.halo?.default?.pressed,
    },
  },
});
